import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Minus } from "lucide-react";
import { cn } from "@/shared/lib/cn";

/** Cómo está de lleno el contenido de un idioma. */
export type LocaleTabState = "complete" | "partial" | "empty";

export interface LocaleTab {
  /** El código del idioma: `"es"`, `"en"`. */
  value: string;
  /** Lo que se lee en la pestaña: "Español", "English". */
  label: string;
  state: LocaleTabState;
}

export interface LocaleTabsProps {
  tabs: readonly LocaleTab[];
  active: string;
  onChange: (value: string) => void;
  /** Id del panel que estas pestañas controlan, para `aria-controls`. */
  panelId: string;
  className?: string;
}

const stateIcons: Record<LocaleTabState, typeof Check> = {
  complete: Check,
  partial: AlertTriangle,
  empty: Minus,
};

const stateClasses: Record<LocaleTabState, string> = {
  complete: "text-ds-success",
  partial: "text-ds-terracotta",
  empty: "text-ds-ink-3",
};

const stateLabels: Record<LocaleTabState, string> = {
  complete: "Completo",
  partial: "Incompleto",
  empty: "Sin traducir",
};

/**
 * Pestañas de idioma con el estado de cada una A LA VISTA.
 *
 * El punto entero del componente es el iconito: sin él hay que abrir
 * cada pestaña para descubrir que el inglés quedó a medias, y nadie lo
 * hace. Con él, "el inglés está incompleto" se ve sin tocar nada.
 *
 * Implementa el patrón `tablist` de ARIA con las flechas del teclado,
 * porque unas pestañas que solo responden al ratón no son pestañas.
 * Candidata a `shared/ui/` — el estado que pinta es genérico.
 */
export function LocaleTabs({
  tabs,
  active,
  onChange,
  panelId,
  className,
}: LocaleTabsProps) {
  const { t } = useTranslation();

  function move(delta: number) {
    const index = tabs.findIndex((tab) => tab.value === active);
    if (index < 0) return;
    const next = tabs[(index + delta + tabs.length) % tabs.length];
    if (next) onChange(next.value);
  }

  return (
    <div
      role="tablist"
      aria-label={t("Idioma del contenido")}
      className={cn("flex gap-1 rounded-ds-pill bg-ds-mist/50 p-1", className)}
      onKeyDown={(event) => {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          move(1);
        } else if (event.key === "ArrowLeft") {
          event.preventDefault();
          move(-1);
        }
      }}
    >
      {tabs.map((tab) => {
        const selected = tab.value === active;
        const Icon = stateIcons[tab.state];
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            id={`${panelId}-tab-${tab.value}`}
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.value)}
            className={cn(
              "inline-flex items-center gap-2 rounded-ds-pill px-4 py-2 text-[13px] font-semibold transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50",
              selected
                ? "bg-ds-surface text-ds-violet-pop shadow-ds-xs"
                : "text-ds-ink-3 hover:text-ds-ink",
            )}
          >
            {tab.label}
            <Icon
              className={cn("h-3.5 w-3.5", stateClasses[tab.state])}
              aria-hidden="true"
            />
            <span className="sr-only">{t(stateLabels[tab.state])}</span>
          </button>
        );
      })}
    </div>
  );
}
